import { useCallback, useEffect, useId, useState } from "react";

import { cx } from "@/lib/cx";
import { Switch } from "@/ui";
import { AUTOSTART_UNSUPPORTED, autostartState, onAutostartChanged, setAutostart } from "./autostart";
import type { AutostartState } from "./types";

/**
 * Settings that only exist because there is a native shell around the bundle.
 *
 * Renders nothing at all in a browser or PWA — the same bundle is served from
 * the VPS for the phone, and a login-item switch there would be a lie. The
 * component asks the platform layer what is supported instead of sniffing for
 * Tauri itself, so a capability that is present but broken also disappears.
 */
export function NativeSettings({ className }: { className?: string }) {
  const [state, setState] = useState<AutostartState>(AUTOSTART_UNSUPPORTED);
  const [busy, setBusy] = useState(false);
  const labelId = useId();

  useEffect(() => {
    let active = true;
    let unlisten: (() => void) | undefined;

    void autostartState().then((next) => {
      if (active) {
        setState(next);
      }
    });

    // The tray menu carries the same toggle; without this the two would drift.
    void onAutostartChanged((next) => {
      if (active) {
        setState(next);
      }
    }).then((off) => {
      if (active) {
        unlisten = off;
      } else {
        off();
      }
    });

    return () => {
      active = false;
      unlisten?.();
    };
  }, []);

  const toggle = useCallback(async () => {
    setBusy(true);
    try {
      // Render whatever actually took effect: the OS can refuse, and pretending
      // otherwise would leave the switch lying about the system's real state.
      setState(await setAutostart(!state.enabled));
    } finally {
      setBusy(false);
    }
  }, [state.enabled]);

  if (!state.supported) {
    return null;
  }

  return (
    <div className={cx("flex flex-col gap-2", className)}>
      {/*
        The shared `Switch`, not a hand-rolled one.
        This file drew its own 32 × 18 track with a `bg-focus` on-state, a
        `bg-raised` knob and a `duration-150 ease-standard` slide — the app's
        only second switch, at a different size, on the dark teal the accent
        split replaced, and outside the motion vocabulary. `Switch` is the same
        control done once: the 51 × 31 iOS geometry, the vivid accent on-state,
        `shadow-knob` under a white thumb, and the spring the rest of the app
        moves on. It owns `role="switch"` and `aria-checked` itself.
      */}
      <label className="flex w-full items-center justify-between gap-3 px-2 py-2 text-label text-muted">
        <span id={labelId}>Start at login</span>
        <Switch
          checked={state.enabled}
          onChange={() => void toggle()}
          labelledBy={labelId}
          disabled={busy}
        />
      </label>
    </div>
  );
}
